import { Injectable } from "@angular/core";
import * as _ from "lodash";
import { combineLatest, from, Observable, of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { Quadrant } from "src/app/core/survey/inside-survey/models/quadrant.enum";
import { PictureInfo } from "src/app/core/survey/shared/components/photo-and-remark/photo-viewmodel";
import { APIEndpoints } from "../../../config/apiEndpoint.config";
import { HttpService } from "../http.service";
import { ImageCompressService } from "../image-compress.service";
import { Address } from "../Models/Address";
import { InsideSurveyPersonTypeEnum } from "../Models/enums/InsideSurveyPersonTypeEnum";
import { PersonLanguageEnum } from "../Models/enums/PersonLanguageEnum";
import { SurveyPhotoTypeEnum } from "../Models/enums/SurveyPhotoTypeEnum";
import { Building, InsideSurvey, Layout } from "../Models/read";
import { InsidePhotoDto } from "../Models/write/InsidePhotoDto";
import { PhotoObjectDto } from "../Models/write/PhotoObjectDto";
import { ChangeBuildingTypeDTO } from "../Models/write/ChangeBuildingTypeDTO";
import { ChangeInsideSurveyInsideUnitsDTO } from "../Models/write/ChangeInsideSurveyInsideUnitsDto";
import { ChangeInsideSurveyPhotoIndexDTO } from "../Models/write/ChangeInsideSurveyPhotoIndexDto";
import { ChangeInsideSurveyPhotoRemarksDto } from "../Models/write/ChangeInsideSurveyPhotoRemarksDto";
import { ChangeInsideUnitInfoDTO } from "../Models/write/changeInsideUnitInfoDto";
import { ChangeOutsideUnitInfoFromInsideSurveyDTO } from "../Models/write/ChangeOutsideUnitInfoFromInsideSurveyDTO";
import { CreateInsideSurveyDTO } from "../Models/write/createInsideSurveyDTO";
import { CreateUnitWithPersonDTO } from "../Models/write/createUnitWithPersonDTO";
import { DeleteInsideUnitDTO } from "../Models/write/deleteInsideUnitsDTO";
import { LayoutDto } from "../Models/write/LayoutDto";
import { RemoveIntroPhotoDto } from "../Models/write/RemoveIntroPhotoDto";
import { OutsidePhotoDto } from "../Models/write/OutsidePhotoDto";
import { UpdateInsideSurveyTechnicalInfoDTO } from "../Models/write/updateInsideSurveyTechnicalInfoDTO";
import {
  SaveValidateInsideSurveyDto,
  ValidateInsideSurveyDto,
} from "../Models/write/ValidateSurveyDto";
import { DataConvertingService } from "./data-converting.service";
import { Coordinate } from "../Models/buildingTableViewModel";

@Injectable({
  providedIn: "root",
})
export class InsideSurveyService {
  constructor(
    private httpService: HttpService,
    private dataService: DataConvertingService,
    private imageCompressor: ImageCompressService
  ) {}

  create(
    insideSurveyId: string,
    buildingId: string,
    buildingGroupId: string,
    insideEngineerId: string
  ) {
    return this.httpService.post(APIEndpoints.createInsideSurvey, {
      insideSurveyId,
      buildingId,
      buildingGroupId,
      insideEngineerId,
    });
  }

  getSurveyInfo(surveyId: string): Observable<InsideSurvey> {
    return this.httpService
      .get<InsideSurvey>(APIEndpoints.getInsideSurvey + surveyId)
      .pipe(
        map((insideSurvey: InsideSurvey) => {
          if (insideSurvey) {
            insideSurvey = Object.assign(new InsideSurvey(), insideSurvey, {
              buildingLayout: Layout.create(insideSurvey.buildingLayout),
            });
            insideSurvey.buildings = insideSurvey.buildings.map(
              (building: Building) =>
                new Building({
                  ...building,
                  persons: building.persons.map((person) =>
                    this.processPerson(person)
                  ),
                  addresses: building.addresses.map((address) =>
                    Object.assign(new Address(), address)
                  ),
                  coordinates: building.coordinates.map((coordinate) =>
                    Object.assign(new Coordinate(), coordinate)
                  ),
                })
            );
          }
          return insideSurvey;
        })
      );
  }

  createInsideSurvey(data: CreateInsideSurveyDTO) {
    return this.httpService.post(APIEndpoints.createInsideSurvey, data);
  }

  savePhotoTab(data: ChangeInsideSurveyPhotoRemarksDto) {
    return this.httpService
      .post(APIEndpoints.changeInsideSurveyPhotoRemarks, data)
      .pipe(map(() => true));
  }

  removePhotoById(id: string) {
    return this.httpService.post(APIEndpoints.removePhotoToInsideSurvey, {
      id,
    });
  }
  removeAllPhotoPointsByPhotoId(id: string) {
    return this.httpService.post(
      APIEndpoints.removeAllPhotoPointsByInsideSurveyPhotoId,
      { id }
    );
  }

  changeInsideUnitInfo(data: ChangeInsideUnitInfoDTO) {
    return this.httpService.post(APIEndpoints.changeInsideUnitInfo, data);
  }

  deleteInsideUnit(data: DeleteInsideUnitDTO) {
    return this.httpService.post(APIEndpoints.deleteInsideUnit, data);
  }

  createUnitWithPerson(data: CreateUnitWithPersonDTO) {
    return this.httpService.post(APIEndpoints.createUnitWithPerson, data);
  }

  updateInsideSurveyTechnicalInfo(data: UpdateInsideSurveyTechnicalInfoDTO) {
    return this.httpService.post(
      APIEndpoints.updateInsideSurveyTechnicalInfo,
      data
    );
  }

  changeOutsideUnitInfoFromInsideSurvey(
    data: ChangeOutsideUnitInfoFromInsideSurveyDTO
  ) {
    return this.httpService.post(
      APIEndpoints.changeOutsideUnitInfoFromInsideSurvey,
      data
    );
  }

  changeInsideUnitInfoFromInsideSurvey(data: ChangeInsideSurveyInsideUnitsDTO) {
    return this.httpService.post(
      APIEndpoints.changeInsideUnitInfoFromInsideSurvey,
      data
    );
  }

  getLayoutPhoto(SurveyId: string) {
    return this.httpService.get(
      APIEndpoints.getInsideSurveyLayoutUrl(SurveyId)
    );
  }

  refactorSurveyPhotos(surveyPhotos) {
    const photos = {
      facadePhotos: [],
      generalPhotos: [],
      facadeDoubtPhotos: [],
      undergroundPhotos: [],
      outsideIntroPhotos: [],
      generalTechnicalInfoPhotos: [],
      outsideCopperIntroPhotos: [],
      insideCopperIntroPhotos: [],
      outsideFibreIntroPhotos: [],
      insideFibreIntroPhotos: [],
      telcoRoomPhotos: [],
      proximusEquipmentPhotos: [],
      floorPlanPhotos: [],
      electricityInTelcoRoomPhotos: [],
      roomAvailableForNewEquipmentPhotos: [],
    };
    surveyPhotos.forEach((p) => {
      const photo = {
        data: {
          originalUrl: p.originalPhotoData,
          url: p.photoData,
          items: p.photoPoints.map((pp) => {
            return new PhotoObjectDto(pp.metadata);
          }),
          id: p.id,
          type: p.type,
          backgroundImageMetadata: p.backgroundImageMetadata,
        },
        generalPhotoRemark: p.generalPhotoRemark,
        proximusEquipmentPhotoRemark: p.proximusEquipmentPhotoRemark,
        outsideCopperIntroPhotoRemark: p.outsideCopperIntroPhotoRemark,
        insideCopperIntroPhotoRemark: p.insideCopperIntroPhotoRemark,
        outsideFibreIntroPhotoRemark: p.outsideFibreIntroPhotoRemark,
        insideFibreIntroPhotoRemark: p.insideFibreIntroPhotoRemark,
        telcoRoomIntroPhotoRemark: p.telcoRoomIntroPhotoRemark,
        greyed: false,
        index: p.index,
      };

      switch (p.type) {
        case SurveyPhotoTypeEnum.Facade:
          photos.facadePhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.OutsideIntro:
          photos.outsideIntroPhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.Underground:
          photos.undergroundPhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.General:
          photos.generalPhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.GeneralTechnicalInfoPhoto:
          photos.generalTechnicalInfoPhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.ElectricityInTelcoRoom:
          photos.electricityInTelcoRoomPhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.RoomAvailableForNewEquipment:
          photos.roomAvailableForNewEquipmentPhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.InsideCopperIntro:
          photos.insideCopperIntroPhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.OutsideCopperIntro:
          photos.outsideCopperIntroPhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.InsideFibreIntro:
          photos.insideFibreIntroPhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.OutsideFibreIntro:
          photos.outsideFibreIntroPhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.TelcoRoom:
          photos.telcoRoomPhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.ProximusEquipment:
          photos.proximusEquipmentPhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.FloorPlan:
          photos.floorPlanPhotos.push(photo);
          break;
      }
    });
    Object.keys(photos).forEach((k) => {
      photos[k].sort((p1: PictureInfo, p2: PictureInfo) => {
        if (!p1.index && !p2.index) return 1;
        else if (!p1.index) return -1;
        else if (!p2.index) return 1;
        else if (p1.index > p2.index) return 1;
        else return -1;
      });
    });
    return photos;
  }

  // draw & gallery
  updateLayout(dto: LayoutDto): Observable<void> {
    return this.httpService.post(
      APIEndpoints.updateInsideSurveyBuildingLayout,
      dto
    );
  }

  getInsideUnits(id: string) {
    return this.httpService.get(APIEndpoints.getInsideUnits(id));
  }

  processPerson(person) {
    person.personType = Object.keys(InsideSurveyPersonTypeEnum).find(
      (key) => InsideSurveyPersonTypeEnum[key] === person.personType
    );
    person.language = Object.keys(PersonLanguageEnum).find(
      (key) => PersonLanguageEnum[key] === person.language
    );
    return person;
  }

  validate(dto: ValidateInsideSurveyDto) {
    return this.httpService.post(APIEndpoints.validateInsideSurvey, dto);
  }

  saveValidation(dto: SaveValidateInsideSurveyDto) {
    return this.httpService.post(APIEndpoints.saveValidateInsideSurvey, dto);
  }

  reject(surveyId: string, validatorRemark: string) {
    const dto = {
      surveyId,
      validatorRemark,
    };
    return this.httpService.post(APIEndpoints.rejectInsideSurvey, dto);
  }

  complete(surveyId: string) {
    return this.httpService.post(APIEndpoints.completeInsideSurvey, {
      surveyId,
    });
  }

  removeIntroPhoto(data: RemoveIntroPhotoDto) {
    return this.httpService.post(APIEndpoints.removeIntroPhoto, data);
  }

  areAdditionalGeneralQuestionsVisible(props: Partial<InsideSurvey>) {
    return _.includes([Quadrant.XC, Quadrant.XA, Quadrant.XD], props.quadrant);
  }

  changeBuildingType(data: ChangeBuildingTypeDTO) {
    return this.httpService.post(APIEndpoints.changeBuildingType, data);
  }

  changeInsideSurveyPhotoIndex(data: ChangeInsideSurveyPhotoIndexDTO) {
    return this.httpService.post(
      APIEndpoints.changeInsideSurveyPhotoIndex,
      data
    );
  }
}
